import { useEffect } from 'react';

import { Box, Group, ScrollArea, Stack, Text } from '@mantine/core';

import { useTranslation } from 'react-i18next';

import { useUrlHash } from '../../../../../../shared/contexts/UrlHash.provider';
import type { DocumentChunk } from '../../../../../../shared/entities/DocumentChunk';
import type { TenderDocument } from '../../../../../../shared/entities/TenderDocument';
import { UrlHashTypes } from '../../../../../../shared/utils/navigation/UrlHashBuilder';
import { SOURCE_SIDE_PANEL_WIDTH } from '../../../../../documents/components/SourceSidePanel';
import { SourcesSection } from '../../../../../documents/components/SourcesSection';
import { useDocumentSidePanel } from '../../../../../documents/hooks/useDocumentSidePanel';
import { useSourceSidePanel } from '../../../../../documents/hooks/useSourceSidePanel';
import { sortDocumentsPerLot } from '../../../../utils/sortDocumentsPerLot';
import { DocumentsAccordion } from '../../../documents/components/DocumentsAccordion';
import { useGetSummaryCriterionDetail } from '../../hooks/useGetSummaryCriterionDetail.hook';
import { CriterionAnswerType, getAnswerType } from '../../utils/getAnswerType';
import { CriterionElement } from './CriterionElement';
import { EditedAtRow } from './EditedAtRow';
import { SummarySheetCollaboration } from './SummarySheetCollaboration';

const MIN_MARGIN_LEFT_TO_ENABLE_USER_TO_CLICK_OUTSIDE = 20;
type AnswerDetailPanelProps = {
  tenderId: number;
  documents: TenderDocument[];
  criterionAnswerId: number;
  closePanel: () => void;
};

export function AnswerDetailPanel({
  tenderId,
  criterionAnswerId,
  documents,
}: AnswerDetailPanelProps) {
  const criterionAnswerDetailQuery = useGetSummaryCriterionDetail(
    tenderId,
    criterionAnswerId,
  );

  const {
    manualAnswerCreatedBy: user,
    manualAnswerCreatedAt: createdAt,
    thread,
    sources,
  } = criterionAnswerDetailQuery.data;

  const {
    openPanel: openSourcePanel,
    panelContext: sourcePanelContext,
    closePanel: closeSourcePanel,
  } = useSourceSidePanel({ sources, documents });
  const sortedDocuments = sortDocumentsPerLot(documents);
  const { openPanel: openDocumentPanel, closePanel: closeDocumentPanel } =
    useDocumentSidePanel({ documents: Object.values(sortedDocuments).flat() });

  const handleOpenDocumentPanel = (selectedDocument: TenderDocument) => {
    closeSourcePanel();
    openDocumentPanel(selectedDocument);
  };

  const handleOpenSourcePanel = (selectedSource: DocumentChunk) => {
    closeDocumentPanel();
    openSourcePanel(selectedSource);
  };

  const answerType = getAnswerType(criterionAnswerDetailQuery.data);

  const { getReceivedUrlHashParam } = useUrlHash();
  const receivedUrlHashParam = getReceivedUrlHashParam(
    UrlHashTypes.SUMMARY_SHEET_ACTION,
  );

  useEffect(() => {
    if (receivedUrlHashParam) {
      const summarySheetCollaboration = document.getElementById(
        'summarySheetCollaboration',
      );
      if (summarySheetCollaboration) {
        summarySheetCollaboration.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [receivedUrlHashParam]);

  return (
    <ScrollArea h="100%" sx={{ flexGrow: 1 }}>
      <Stack
        p="04"
        pt={0}
        spacing="04"
        w={`calc(100vw - ${SOURCE_SIDE_PANEL_WIDTH + MIN_MARGIN_LEFT_TO_ENABLE_USER_TO_CLICK_OUTSIDE}px)`}
        miw={420}
        maw={856}
      >
        <CriterionElement
          criterionAnswer={criterionAnswerDetailQuery.data}
          tenderId={tenderId}
        />
        {user && createdAt && (
          <EditedAtRow
            user={user}
            date={createdAt}
            thread={thread}
            criterionAnswerId={criterionAnswerId}
            tenderId={tenderId}
          />
        )}
        {sources &&
          sources.length > 0 &&
          answerType === CriterionAnswerType.LLM && (
            <SourcesSection
              sources={sources}
              openPanel={handleOpenSourcePanel}
              panelContext={sourcePanelContext}
            />
          )}
        <SummarySheetCollaboration
          thread={thread}
          summaryCriterionAnswerId={criterionAnswerId}
          tenderId={tenderId}
        />
        <DocumentSections
          sortedDocuments={sortedDocuments}
          openPanel={handleOpenDocumentPanel}
        />
      </Stack>
    </ScrollArea>
  );
}

type DocumentSectionsProps = {
  sortedDocuments: Record<string, TenderDocument[]>;
  openPanel: (selectedDocument: TenderDocument) => void;
};

function DocumentSections({
  sortedDocuments,
  openPanel,
}: DocumentSectionsProps) {
  const { t } = useTranslation(['notice', 'summarySheet']);

  const numberOfDocument = Object.values(sortedDocuments).flat().length;

  return (
    <Stack>
      <Group noWrap spacing="02">
        <Text variant="sm" fw="500" c="gray.6">
          {t('summarySheet:answerDetailPanel.documentsSection.title')}
        </Text>
        <Box
          w="01"
          h="01"
          bg="gray.2"
          sx={theme => ({ borderRadius: theme.radius.lg })}
        />
        <Text variant="sm" fw="400" c="gray.6">
          {t('documents.numberOfDocuments', {
            count: numberOfDocument,
          })}
        </Text>
      </Group>
      {Object.entries(sortedDocuments).map(([section, documents]) => {
        return (
          <DocumentsAccordion
            key={section}
            documents={documents}
            title={section}
            sortBy="fileExtension"
            onDocumentClick={openPanel}
          />
        );
      })}
    </Stack>
  );
}
